<template>

  <NavigationContainer
    :breadcrumbsItems="formattedBreadcrumbs"
    show-upload-button
    @search-value-changed="searchValueChanged"
    @cleared-search="fetchData"
  >

    <template slot="content">
      <v-container
        v-if="loading || entries.length === 0"
        class="loading-container"
      >
        <v-row
          class="fill-height"
          align-content="center"
          justify="center"
        >
          <v-col
            class="text-subtitle-1 text-center"
            cols="12"
          >
            <span v-if="loading">{{ $t('retrieving_files') }}</span>

            <span v-else>{{ $t('empty_folder') }}</span>
          </v-col>

          <v-col
            v-if="loading"
            cols="6"
          >
            <v-progress-linear
              color="primary"
              indeterminate
              rounded
              height="6"
            ></v-progress-linear>
          </v-col>
        </v-row>
      </v-container>

      <template v-else>
        <NavigationGridView
          v-if="contentPersonalPreferences.navigationViewMode === 'grid'"
          :loading="loading"
          :entries="entries"
          @enter-folder="navigateTo"
        />

        <NavigationListView
          v-else-if="contentPersonalPreferences.navigationViewMode === 'list'"
          :loading="loading"
          :entries="entries"
          :users="users"
          @enter-folder="navigateTo"
        />
      </template>
    </template>

  </NavigationContainer>

</template>

<style scoped>
  .container {
    padding: 0;
  }

  .loading-container {
    height: 400px;
  }
</style>

<script>

import { mapState, mapActions, mapMutations } from 'vuex'
import router from "@/router"

import NavigationContainer from '@/components/content/NavigationContainer.vue'
import NavigationGridView from '@/components/content/navigation/grid/NavigationGridView.vue'
import NavigationListView from '@/components/content/navigation/list/NavigationListView.vue'

import EntryActions from '@/store/content/entry/actions-types'
import EntryMutations from '@/store/content/entry/mutations-types'
import UserActions from '@/store/core/user/actions-types'
import NodeLockActions from '@/store/node/nodelock/actions-types'

import { BgActionEventBus } from '@/bgActionEventBus'

export default {
  name: "Navigation",

  components: {
    NavigationContainer,
    NavigationGridView,
    NavigationListView,
  },

  watch: {
    '$route.params.parentId': {
      handler: function(parentId) {
        this.fetchData(parentId)
      },
      deep: true,
      immediate: true,
    },
    breadcrumb: {
      handler: function() {
        this.checkLocks()
      },
      deep: true,
    }
  },

  data() {
    return {
      loading: true,
      orderBy: {
        name: null,
        updated_at: null,
      },
      users: [],
      dragInternal: false,
      draggedList: [],
      droppedNode: null,
      lockedNodes: [],
      lockedNodesMap: {}, // node_id -> lock mode (e.g. 'shared', 'exclusive', 'parent')
      showBreadcrumbLockIcon: false,
      bgActionHandler: null,
      entryListLoadPromise: null,
    }
  },

  mounted() {
    this.bgActionHandler = (bgAction) => this.handleBgAction(bgAction)

    BgActionEventBus.$on('bgAction', this.bgActionHandler)
    if (this.entryListLoadPromise) {
      this.entryListLoadPromise.finally(() => this.replayPendingUploadCleanupActions())
    } else {
      this.replayPendingUploadCleanupActions()
    }
  },

  computed: {
    ...mapState({
      breadcrumb: state => state.content.entry.breadcrumb,
      entries: state => state.content.entry.entries,
      contentPersonalPreferences: state => state.app.contentPersonalPreferences,
    }),

    formattedBreadcrumbs() {
      let formatted = [
        {
          prependIcon: 'mdi-home-outline',
          iconColor: 'primary',
          to: {
            name: 'ContentHome',
            params: { appId: this.$route.params.appId }
          },
          link: true,
          disabled: false,
          ripple: false,
          tooltipText: "Home",
        },
      ]

      const reversed = [].concat(this.breadcrumb).reverse()

      reversed.forEach(element => {
        const isLocked = this.lockedNodes.includes(element.node_id)
        const lockType = this.lockedNodesMap[element.node_id] || null
        formatted.push({
          to: {
            name: 'ContentNavigation',
            params: { parentId: element.node_id }
          },
          text: element.name,
          ripple: false,
          isLocked: this.showBreadcrumbLockIcon && isLocked,
          lockType: lockType,
        })
      })

      return formatted
    },
  },

  methods: {
    ...mapActions('content/entry', [
      EntryActions.LIST_SUBFOLDER,
      EntryActions.UPSERT_ENTRY_LIST,
      EntryActions.SEARCH_BY_TERM
    ]),

    ...mapActions('core/user', [
      UserActions.GET_ALL_USERS,
    ]),

    ...mapActions('node/nodelock', [
      NodeLockActions.CHECK_NODE,
    ]),

    ...mapMutations('content/entry', [
      EntryMutations.REMOVE_ENTRY,
    ]),

    handleBgAction(message) {
      const parentId = this.$route.params.parentId

      if (!this.isCompletedBgActionMessage(message) || parentId === null || parentId === undefined) {
        return
      }

      if (this.isCurrentBgActionRoom(message, parentId)) {
        this.handleCompletedBgActionMessage(message, parentId)
      }
    },

    handleCompletedBgActionMessage(message, parentId) {
      // ZIP downloads do not change a navigation entry. Their completion is
      // handled by SelectedItemsBar, which reads the URL from message.text.
      if (this.isShareDownloadBgActionMessage(message)) {
        return
      }

      const nodeId = this.getBgActionNodeId(message)
      const isDone = String(message.status).toLowerCase() === 'done'

      if (!nodeId) {
        console.warn('[bgAction:webapp] completed message ignored because node_id is missing', { message })
        return
      }

      if (this.isNodeLockBgActionMessage(message) && !this.isEntryDisplayed(nodeId)) {
        return
      }

      if (isDone && this.isDeleteBgActionMessage(message)) {
        this[EntryMutations.REMOVE_ENTRY](nodeId)
        return
      }

      this[EntryActions.UPSERT_ENTRY_LIST]({parentId, nodeId})
        .then((nodeData) => {
          if (isDone && !nodeData && this.shouldRemoveWhenUpsertMisses(message)) {
            this[EntryMutations.REMOVE_ENTRY](nodeId)
          }
        })
        .catch((error) => {
          console.error('[bgAction:webapp] targeted entry refresh failed', { nodeId, error })
        })
    },

    getBgActionParentNodeId(message) {
      return message.node_parent
        ?? message.nodeParent
        ?? message.parent_node_id
        ?? message.parentNodeId
        ?? message.node_parent_id
        ?? message.nodeParentId
        ?? message.folder_parent_id
        ?? message.folderParentId
    },

    getBgActionNodeId(message) {
      return message.node_id
        ?? message.nodeId
    },

    isDeleteBgActionMessage(message) {
      return ['delete', 'remove'].includes(String(message.type).toLowerCase())
    },

    isShareDownloadBgActionMessage(message) {
      return ['share-download', 'share_download'].includes(String(message.type).toLowerCase())
    },

    shouldRemoveWhenUpsertMisses(message) {
      return ['move'].includes(String(message.type).toLowerCase())
    },

    isNodeLockBgActionMessage(message) {
      return ['node_lock', 'node-lock'].includes(String(message.type).toLowerCase())
    },

    isEntryDisplayed(nodeId) {
      return this.entries.some(entry => String(entry.node_id) === String(nodeId))
    },

    replayPendingUploadCleanupActions() {
      const parentId = this.$route.params.parentId

      if (parentId === null || parentId === undefined) {
        return
      }

      try {
        const storageKey = 'pending-upload-cleanup-bg-actions'
        const pendingMessages = JSON.parse(sessionStorage.getItem(storageKey) || '[]')
        const [messagesToReplay, remainingMessages] = pendingMessages.reduce(
          ([matching, remaining], message) => {
            if (String(this.getBgActionParentNodeId(message)) === String(parentId)) {
              matching.push(message)
            } else {
              remaining.push(message)
            }
            return [matching, remaining]
          },
          [[], []]
        )

        if (remainingMessages.length > 0) {
          sessionStorage.setItem(storageKey, JSON.stringify(remainingMessages))
        } else {
          sessionStorage.removeItem(storageKey)
        }

        messagesToReplay.forEach(message => BgActionEventBus.$emit('bgAction', message))
      } catch (error) {
        console.error('[upload] unable to replay cleanup refresh', error)
      }
    },

    isCurrentBgActionRoom(message, parentId) {
      const messageParentNodeId = this.getBgActionParentNodeId(message)

      if (messageParentNodeId === null || messageParentNodeId === undefined) {
        return false
      }

      return String(messageParentNodeId) === String(parentId)
    },

    isCompletedBgActionMessage(message) {
      return ['success', 'done', 'error'].includes(String(message.status).toLowerCase())
    },

    fetchData(parentId) {
      this.loading = true

      const usersOptions = {
        'filter[include_foreign]': true,
        'limit': 9999,
      }

      const usersPromise = this[UserActions.GET_ALL_USERS](usersOptions)
      usersPromise.then((result) => {
        this.users = [...result.items]
      })

      const entriesPromise = this[EntryActions.LIST_SUBFOLDER]({parentId})
      
      this.entryListLoadPromise = Promise.all([usersPromise, entriesPromise])
        .finally(() => this.loading = false)

      return this.entryListLoadPromise
    },

    navigateTo(item) {
      router.push({
        name: 'ContentNavigation',
        params: { parentId: item.node_id }
      })
    },

    searchValueChanged({term, baseNodeId}, showBreadcrumb = false) {
      this.loading = true

      const params = {
        term,
        'filters[base_node_id]': baseNodeId
      }

      this[EntryActions.SEARCH_BY_TERM]([params, showBreadcrumb])
        .finally(() => { this.loading = false })
    },

    checkLocks() {
      // Extract node_ids from the breadcrumb hierarchy
      const nodeIds = this.breadcrumb.map(item => item.node_id)
      
      // Only check if we have nodes in the hierarchy
      if (nodeIds.length === 0) {
        this.lockedNodes = []
        return
      }

      const payload = { base_array: nodeIds }

      // Call the CHECK_NODE action with the list of node_ids
      this[NodeLockActions.CHECK_NODE](payload)
        .then((response) => {

          // The API returns an array: [{node_lock_id, node_id, mode, ...}, ...]
          const locks = Array.isArray(response) ? response : []

          if (locks.length > 0) {
            this.lockedNodes = [...new Set(locks.map(lock => lock.node_id))]
            const map = {}
            locks.forEach(lock => {
              map[lock.node_id] = lock.mode || null
            })
            this.lockedNodesMap = map
          } else {
            this.lockedNodes = []
            this.lockedNodesMap = {}
          }
        })
        .catch(() => {
          // On error, assume no locks
          this.lockedNodes = []
        })
    },
  },

  updated() {
    if(this.breadcrumb.length > 0 && this.breadcrumb[0].name)
      document.title = this.breadcrumb[0].name + " | Jamworks 2.0"
  },

  beforeDestroy() {
    if (this.bgActionHandler) {
      BgActionEventBus.$off('bgAction', this.bgActionHandler)
    }

    document.title = "Jamworks 2.0"
  }
}

</script>
